/**
 * Arrondi du comptant a 0,05 $ et monnaie a rendre (plan 1.1 et 2, PR 1.5b ;
 * design 4.4). Fonctions pures, montants en ENTIERS DE CENTS.
 *
 * Regle canadienne, sur le dernier chiffre des cents du total :
 *   1 et 2 -> vers le bas a 0      (47,21 -> 47,20 ; 47,22 -> 47,20)
 *   3 et 4 -> vers le haut a 5     (47,23 -> 47,25 ; 47,24 -> 47,25)
 *   6 et 7 -> vers le bas a 5      (47,26 -> 47,25 ; 47,27 -> 47,25)
 *   8 et 9 -> vers le haut a 10    (47,28 -> 47,30 ; 47,29 -> 47,30)
 *   0 et 5 -> inchange
 *
 * Signe : celui du contrat (vente/contrat.js, PaiementComptant) :
 * cash_rounding_cents = amount_due_cents - total_cents, de -2 a +2. Une vente
 * de 0,99 $ payee 1,00 $ porte donc +1 ici. Attention, Lightspeed enregistre
 * le meme arrondi comme un paiement « Cash Rounding » de signe CONTRAIRE
 * (-0,01 $ pour cette vente) : a inverser le jour ou une vente de secours est
 * ressaisie ou reinjectee dans Lightspeed.
 */

/** Ajustement par dernier chiffre des cents (index 0 a 9). */
const AJUSTEMENT = Object.freeze([0, -1, -2, 2, 1, 0, -1, -2, 2, 1]);

/** Plafond de saisie d'un montant recu : 99 999,99 $. */
export const MONTANT_RECU_MAX_CENTS = 9_999_999;

function verifierCents(cents, nom) {
    if (!Number.isSafeInteger(cents)) throw new TypeError(`${nom} : cents entiers attendus`);
    if (cents < 0) throw new RangeError(`${nom} : montant negatif refuse`);
}

/**
 * @param {number} totalCents totals.total_cents du panier (taxes comprises), >= 0
 * @returns {{ cash_rounding_cents: number, amount_due_cents: number }}
 */
export function arrondirComptant(totalCents) {
    verifierCents(totalCents, 'arrondirComptant');
    const ajustement = AJUSTEMENT[totalCents % 10];
    return { cash_rounding_cents: ajustement, amount_due_cents: totalCents + ajustement };
}

/**
 * Monnaie a rendre, ou null tant que le montant recu ne couvre pas le
 * montant du (la vente ne peut pas etre terminee).
 * @param {number} amountDueCents montant du apres l'arrondi
 * @param {number} cashReceivedCents montant recu
 * @returns {number|null}
 */
export function monnaieARendre(amountDueCents, cashReceivedCents) {
    verifierCents(amountDueCents, 'monnaieARendre (du)');
    verifierCents(cashReceivedCents, 'monnaieARendre (recu)');
    return cashReceivedCents >= amountDueCents ? cashReceivedCents - amountDueCents : null;
}

/**
 * Paiement comptant complet (PaiementComptant du contrat). Refuse un paiement
 * incomplet : aucune vente sans paiement complet.
 * @param {number} totalCents
 * @param {number} cashReceivedCents
 */
export function paiementComptant(totalCents, cashReceivedCents) {
    const { cash_rounding_cents, amount_due_cents } = arrondirComptant(totalCents);
    if (Number.isSafeInteger(cashReceivedCents) && cashReceivedCents > MONTANT_RECU_MAX_CENTS) {
        throw new RangeError('paiementComptant : montant recu trop grand');
    }
    const change = monnaieARendre(amount_due_cents, cashReceivedCents);
    if (change === null) throw new RangeError('paiementComptant : montant recu insuffisant');
    return {
        mode: 'COMPTANT',
        cash_rounding_cents,
        amount_due_cents,
        cash_received_cents: cashReceivedCents,
        change_given_cents: change,
    };
}
